import { useState, useEffect } from "react";
import GameBoard from "./GameBoard";
import WordDefinitionModal from "./WordDefinitionModal";
import { fetchJson } from "../utils/apiClient";
import { getGuessStatuses } from "../utils/wordUtils";
import { API_BASE, FLIP_DURATION, FLIP_STAGGER } from "../constants/gameConstants";

function computeKbs(guesses, targetWord) {
  const kbs = {};
  guesses.forEach(guess => {
    getGuessStatuses(guess, targetWord).forEach((status, i) => {
      const letter = guess[i];
      if (!kbs[letter] || status === "correct" || (status === "present" && kbs[letter] !== "correct"))
        kbs[letter] = status;
    });
  });
  return kbs;
}

function handleShareResult({ playerName, completedHoles, totalScore }) {
  const scoreText = totalScore > 0 ? `+${totalScore}` : totalScore === 0 ? 'E' : `${totalScore}`;
  let shareText = `⚡ Grordle Golf Express - ${playerName}\nTotal Score: ${scoreText}\n\n`;
  completedHoles.forEach(hole => {
    const holeScore = hole.score === 0 ? 'E' : hole.score > 0 ? `+${hole.score}` : `${hole.score}`;
    const emoji = hole.score < 0 ? '🟢' : hole.score === 0 ? '🔵' : '🔴';
    const hint  = hole.hint_used ? '💡' : '';
    const failed = hole.score === (hole.attempts + 1 - hole.par);
    const attemptText = failed ? 'Failed' : `${hole.attempts} ${hole.attempts === 1 ? 'shot' : 'shots'}`;
    shareText += `${emoji}${hint} Hole ${hole.hole_number}: Par ${hole.par} → ${attemptText} (${holeScore})\n`;
  });
  if (navigator?.clipboard) navigator.clipboard.writeText(shareText);
  window.open('https://wa.me/?text=' + encodeURIComponent(shareText), '_blank');
}

export default function GolfExpressGame({ playerName, setMessage, validationWords }) {
  const [isViewingMode, setIsViewingMode]         = useState(false);
  const [showDefinition, setShowDefinition]       = useState(false);
  const [savedGameState, setSavedGameState]       = useState(null);
  const [viewingHoleNumber, setViewingHoleNumber] = useState(null);
  const [roundId, setRoundId]                     = useState(null);
  const [currentHole, setCurrentHole]             = useState(1);
  const [targetWord, setTargetWord]               = useState("");
  const [par, setPar]                             = useState(3);
  const [currentGuess, setCurrentGuess]           = useState("");
  const [guesses, setGuesses]                     = useState([]);
  const [gameOver, setGameOver]                   = useState(false);
  const [keyboardStatus, setKeyboardStatus]       = useState({});
  const [completedHoles, setCompletedHoles]       = useState([]);
  const [roundCompleted, setRoundCompleted]       = useState(false);
  const [isLoading, setIsLoading]                 = useState(false);
  const [revealRowIndex, setRevealRowIndex]       = useState(null);
  const [shakeRowIndex, setShakeRowIndex]         = useState(null);
  const [isRevealing, setIsRevealing]             = useState(false);
  const [winningRowIndex, setWinningRowIndex]     = useState(null);
  const [hasInitialized, setHasInitialized]       = useState(false);
  const [hintUsed, setHintUsed]                   = useState(false);
  const [hintClue, setHintClue]                   = useState(null);
  const [hintVisible, setHintVisible]             = useState(true);
  const [holeSuccess, setHoleSuccess]             = useState(null);

  useEffect(() => { return () => setHasInitialized(false); }, []);

  // Init round
  useEffect(() => {
    if (!playerName || hasInitialized || isViewingMode) return;
    const initRound = async () => {
      setIsLoading(true);
      try {
        const result = await fetchJson(`${API_BASE}/golf-express-game-state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerName })
        });
        if (!result.ok) return;

        setRoundId(result.roundId);
        setCurrentHole(result.currentHole);
        setCompletedHoles(result.completedHoles || []);

        if (result.roundCompleted) {
          setRoundCompleted(true);
          setHasInitialized(true);
          return;
        }

        if (result.currentHoleData) {
          const { targetWord: tw, par: p, guesses: g, hintUsed: hu } = result.currentHoleData;
          setTargetWord(tw);
          setPar(p);
          const savedGuesses = Array.isArray(g) ? g : [];
          setGuesses(savedGuesses);
          setCurrentGuess("");
          setGameOver(false);
          setMessage("");
          setKeyboardStatus(computeKbs(savedGuesses, tw));
          setHintUsed(hu || false);
          setHintClue(result.currentHoleData.hintClue || null);
          setHintVisible(true);
        }
        setHasInitialized(true);
      } catch (err) {
        console.error("GolfExpress init error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    initRound();
  }, [playerName, hasInitialized, isViewingMode]);

  // Physical keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver || isRevealing || isLoading || isViewingMode) return;
      const key = e.key.toUpperCase();
      if (key === "ENTER") { e.preventDefault(); handleSubmitGuess(); }
      else if (key === "BACKSPACE") { e.preventDefault(); setCurrentGuess(p => p.slice(0, -1)); }
      else if (key === "ESCAPE")  { e.preventDefault(); setCurrentGuess(""); }
      else if (/^[A-Z]$/.test(key) && currentGuess.length < 5) { e.preventDefault(); setCurrentGuess(p => p + key); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentGuess, gameOver, isRevealing, isLoading, isViewingMode]);

  const loadNextHole = async (rid) => {
    const id = rid || roundId;
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await fetchJson(`${API_BASE}/golf-express-next-hole`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId: id })
      });
      if (result.ok) {
        setTargetWord(result.targetWord);
        setPar(result.par);
        const savedGuesses = Array.isArray(result.guesses) ? result.guesses : [];
        setGuesses(savedGuesses);
        setKeyboardStatus(computeKbs(savedGuesses, result.targetWord));
        setHintUsed(result.hintUsed || false);
        setHintClue(result.hintClue || null);
        setHintVisible(true);
        setCurrentGuess("");
        setGameOver(false);
        setHoleSuccess(null);
        setRevealRowIndex(null);
        setWinningRowIndex(null);
        setMessage(`Hole ${result.holeNumber} - Par ${result.par}`);
      }
    } catch (err) {
      setMessage(`Error loading hole: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitGuess = async () => {
    if (currentGuess.length !== 5) { setMessage("Guess must be 5 letters"); return; }

    if (validationWords) {
      if (!validationWords.has(currentGuess)) {
        setShakeRowIndex(guesses.length);
        setMessage(`"${currentGuess}" is not in the word list`);
        setTimeout(() => { setShakeRowIndex(null); setMessage(""); }, 600);
        return;
      }
    } else {
      try {
        const resp = await fetch("/api/validate-word", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ word: currentGuess })
        });
        const data = await resp.json();
        if (!data.valid) {
          setShakeRowIndex(guesses.length);
          setMessage(`"${currentGuess}" is not in the word list`);
          setTimeout(() => { setShakeRowIndex(null); setMessage(""); }, 600);
          return;
        }
      } catch { setMessage("Error validating word"); return; }
    }

    const newGuesses = [...guesses, currentGuess];
    setRevealRowIndex(newGuesses.length - 1);
    setIsRevealing(true);
    setGuesses(newGuesses);
    setCurrentGuess("");

    try {
      await fetchJson(`${API_BASE}/golf-express-save-guesses`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, holeNumber: currentHole, guesses: newGuesses })
      });
    } catch { /* non-blocking */ }

    const revealTime = FLIP_DURATION + (4 * FLIP_STAGGER);
    setTimeout(() => {
      setIsRevealing(false);
      setRevealRowIndex(null);

      setKeyboardStatus(computeKbs(newGuesses, targetWord));

      const isWin  = currentGuess === targetWord;
      const isLoss = newGuesses.length >= 6 && !isWin;
      if (isWin)       { setWinningRowIndex(newGuesses.length - 1); handleHoleComplete(newGuesses.length, true); }
      else if (isLoss) { handleHoleComplete(newGuesses.length, false); }
    }, revealTime);
  };

  const handleHoleComplete = async (attempts, success) => {
    setGameOver(true);
    setHoleSuccess(success);
    try {
      const result = await fetchJson(`${API_BASE}/golf-express-submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, holeNumber: currentHole, attempts, success })
      });
      if (result.ok) {
        const score = result.score;
        const scoreText = score === 0 ? "Par" : score > 0 ? `+${score}` : `${score}`;
        const newHole = { hole_number: currentHole, par, attempts, score, hint_used: hintUsed };
        setCompletedHoles(prev => [...prev.filter(h => h.hole_number !== currentHole), newHole].sort((a, b) => a.hole_number - b.hole_number));
        if (result.roundCompleted) {
          setRoundCompleted(true);
          setHasInitialized(true);
          setMessage(`${!success ? `The word was: ${targetWord}. ` : ''}Round Complete! Total Score: ${result.totalScore > 0 ? '+' : ''}${result.totalScore}`);
        } else {
          setMessage(`${!success ? `The word was: ${targetWord}. ` : ''}Hole ${currentHole} Complete! Score: ${scoreText}. Click 'Next Hole' to continue.`);
        }
      }
    } catch { setMessage("Error submitting score. Please try again."); }
  };

  const handleNextHole = () => { setCurrentHole(prev => prev + 1); loadNextHole(roundId); };

  const handleGetHint = async () => {
    try {
      const result = await fetchJson(`${API_BASE}/golf-express-hint`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, holeNumber: currentHole })
      });
      if (result.ok) {
        setHintUsed(true);
        setHintClue(result.clue || "No clue available");
      } else {
        setMessage(result.error || "Hint not available");
        setTimeout(() => setMessage(""), 2000);
      }
    } catch { setMessage("Error fetching hint"); setTimeout(() => setMessage(""), 2000); }
  };

  const handleKeyPress = (key) => {
    if (gameOver || isRevealing || isViewingMode) return;
    if (key === "ENTER")     handleSubmitGuess();
    else if (key === "CLEAR" || key === "BACKSPACE") setCurrentGuess(p => key === "CLEAR" ? "" : p.slice(0, -1));
    else if (currentGuess.length < 5) setCurrentGuess(p => p + key);
  };

  const handleViewHole = async (holeNumber) => {
    if (!roundId || (holeNumber === currentHole && !gameOver)) return;
    setShowDefinition(false);
    setIsLoading(true);
    try {
      const result = await fetchJson(`${API_BASE}/golf-express-get-hole`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, holeNumber })
      });
      if (result.ok && result.holeData) {
        if (!isViewingMode) {
          setSavedGameState({ currentHole, targetWord, par, guesses, currentGuess, gameOver, keyboardStatus, hintUsed, hintClue, hintVisible, holeSuccess });
        }
        const { holeData } = result;
        setIsViewingMode(true);
        setViewingHoleNumber(holeNumber);
        setTargetWord(holeData.targetWord);
        setPar(holeData.par);
        setGuesses(holeData.guesses || []);
        setCurrentGuess("");
        setGameOver(true);
        setKeyboardStatus(computeKbs(holeData.guesses || [], holeData.targetWord));
        setHintUsed(holeData.hintUsed || false);
        setHintClue(holeData.hintClue || null);
        setHintVisible(true);
        setHoleSuccess(holeData.success ?? null);
        setMessage(holeData.isCompleted
          ? (holeData.success ? `Viewing Hole ${holeNumber} - Completed in ${holeData.attempts} attempts`
                              : `Viewing Hole ${holeNumber} - Failed. The word was: ${holeData.targetWord}`)
          : `Viewing Hole ${holeNumber}`);
      }
    } catch { setMessage("Error loading hole"); }
    finally { setIsLoading(false); }
  };

  const handleExitViewingMode = async () => {
    const snapshot = savedGameState;
    if (!snapshot) { setIsViewingMode(false); setViewingHoleNumber(null); return; }
    setIsLoading(true);
    const applyState = ({ holeData, fallback }) => {
      setIsViewingMode(false);
      setViewingHoleNumber(null);
      const src = holeData || fallback;
      setCurrentHole(snapshot.currentHole);
      setTargetWord(src.targetWord || fallback.targetWord);
      setPar(src.par ?? fallback.par);
      const g = holeData ? (holeData.guesses || []) : fallback.guesses;
      setGuesses(g);
      setCurrentGuess(holeData ? "" : fallback.currentGuess);
      setGameOver(holeData ? holeData.isCompleted : fallback.gameOver);
      const tw = src.targetWord || fallback.targetWord;
      setKeyboardStatus(holeData ? computeKbs(g, tw) : fallback.keyboardStatus);
      setHintUsed(holeData ? (holeData.hintUsed || false) : (fallback.hintUsed || false));
      setHintClue(holeData ? (holeData.hintClue || null) : (fallback.hintClue || null));
      setHintVisible(true);
      setHoleSuccess(holeData ? (holeData.isCompleted ? (holeData.success ?? null) : null) : (fallback.holeSuccess ?? null));
      setSavedGameState(null);
      setRevealRowIndex(null); setShakeRowIndex(null);
      setWinningRowIndex(null); setIsRevealing(false);
      setMessage("");
      setIsLoading(false);
    };
    try {
      const result = await fetchJson(`${API_BASE}/golf-express-get-hole`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, holeNumber: snapshot.currentHole })
      });
      applyState(result.ok && result.holeData ? { holeData: result.holeData, fallback: snapshot } : { holeData: null, fallback: snapshot });
    } catch {
      applyState({ holeData: null, fallback: snapshot });
    }
  };

  // ── Scorecard helpers ──────────────────────────────────────────────────────
  const getScoreLabel = (score) => score === 0 ? "E" : score > 0 ? `+${score}` : `${score}`;

  const scorecardCell = (holeNum) => {
    const hole = completedHoles.find(h => h.hole_number === holeNum);
    const isActive  = holeNum === currentHole && !isViewingMode;
    const isViewing = isViewingMode && holeNum === viewingHoleNumber;
    const isCompleted = hole && hole.score !== null;
    const isClickable = isCompleted && (!isActive || isViewingMode);
    return (
      <div
        key={holeNum}
        onClick={() => { if (isClickable) handleViewHole(holeNum); }}
        className={`text-center p-2 rounded ${
          isViewing   ? 'bg-purple-500 text-white font-bold ring-2 ring-purple-300' :
          isActive    ? 'bg-blue-500 text-white font-bold' :
          isCompleted ? (hole.score < 0 ? 'bg-green-500/50 text-white' : hole.score === 0 ? 'bg-blue-500/50 text-white' : 'bg-red-500/50 text-white') :
          'bg-white/10 text-gray-400'
        } ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-white/50 transition' : ''}`}
      >
        <div className="text-xs">#{holeNum}</div>
        {isCompleted ? (
          <>
            <div className="text-xs">Par {hole.par}</div>
            <div className="text-xs font-bold">{getScoreLabel(hole.score)}</div>
            {hole.hint_used && <div className="text-xs leading-none">💡</div>}
          </>
        ) : (
          <div className="text-xs opacity-50">-</div>
        )}
      </div>
    );
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-white">
        <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mb-6"></div>
        <div className="text-2xl font-bold mb-2">⚡ Loading Golf Express</div>
        <div className="text-white/60">{isViewingMode ? "Reviewing hole..." : "Setting up your round..."}</div>
      </div>
    );
  }

  // ── Round complete summary ─────────────────────────────────────────────────
  if (roundCompleted && !isViewingMode) {
    const totalScore = completedHoles.reduce((s, h) => s + h.score, 0);
    return (
      <div className="space-y-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-2xl">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">⚡ Round Complete!</h2>
          <div className="bg-white/20 rounded-xl p-6 mb-6">
            <h3 className="text-2xl font-bold text-white text-center mb-4">
              Total Score: {totalScore > 0 ? '+' : ''}{totalScore}
            </h3>
            <div className="text-white/80 text-center mb-4">Click any hole to review</div>
            <div className="space-y-2">
              {completedHoles.map(hole => {
                const scoreText = getScoreLabel(hole.score);
                const colorClass = hole.score < 0 ? "text-green-400" : hole.score === 0 ? "text-blue-400" : "text-red-400";
                const failed = hole.score === (hole.attempts + 1 - hole.par);
                return (
                  <div key={hole.hole_number} onClick={() => handleViewHole(hole.hole_number)}
                    className="flex justify-between items-center bg-white/10 rounded p-3 cursor-pointer hover:bg-white/20 transition">
                    <span className="text-white font-semibold">Hole {hole.hole_number} {hole.hint_used ? "💡" : ""}</span>
                    <span className={`font-bold ${hole.par === 3 ? "text-green-300" : hole.par === 4 ? "text-yellow-300" : "text-red-300"}`}>Par {hole.par}</span>
                    <span className="text-white">{failed ? 'Failed' : `${hole.attempts} ${hole.attempts === 1 ? 'shot' : 'shots'}`}</span>
                    <span className={`font-bold ${colorClass}`}>{scoreText}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center mt-6">
              <button className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition text-lg"
                onClick={() => handleShareResult({ playerName, completedHoles, totalScore })}>
                SHARE COURSE RESULT
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Viewing mode (round complete) ──────────────────────────────────────────
  if (roundCompleted && isViewingMode) {
    return (
      <div className="space-y-6">
        <button onClick={() => { setIsViewingMode(false); setViewingHoleNumber(null); setSavedGameState(null); setMessage(""); }}
          className="w-full py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600 transition">
          ← Back to Round Summary
        </button>
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-xl font-bold text-white">Viewing Hole {viewingHoleNumber} of 9 {hintUsed ? "💡" : ""}</h3>
            <div className="text-lg font-bold text-yellow-400">Par {par}</div>
          </div>
          <div className="grid grid-cols-9 gap-1 mb-2">
            {[1,2,3,4,5,6,7,8,9].map(scorecardCell)}
          </div>
        </div>
        {targetWord && (
          <div className="flex justify-center">
            <button className="px-4 py-2 bg-purple-600 text-white rounded font-bold hover:bg-purple-700 transition"
              onClick={(e) => { e.stopPropagation(); setShowDefinition(true); }}>
              Define
            </button>
          </div>
        )}
        <GameBoard guesses={guesses} currentGuess="" todayWord={targetWord} targetWord={targetWord}
          maxGuesses={6} revealRowIndex={revealRowIndex} shakeRowIndex={shakeRowIndex}
          winningRowIndex={winningRowIndex} gameOver={true} keyboardStatus={keyboardStatus}
          onKeyPress={() => {}} isRevealing={isRevealing} />
        {showDefinition && targetWord && <WordDefinitionModal word={targetWord} onClose={() => setShowDefinition(false)} />}
      </div>
    );
  }

  // ── Active / in-progress round ─────────────────────────────────────────────
  const showHintClue = !!hintClue;

  return (
    <div className="space-y-6 pb-20">
      {/* Scorecard */}
      <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xl font-bold text-white">
            {isViewingMode ? `Viewing Hole ${viewingHoleNumber} of 9` : `Hole ${currentHole} of 9`}
            {hintUsed && " 💡"}
          </h3>
          <div className="text-lg font-bold text-yellow-400">Par {par}</div>
        </div>
        <div className="grid grid-cols-9 gap-1 mb-2">
          {[1,2,3,4,5,6,7,8,9].map(scorecardCell)}
        </div>
      </div>

      {isViewingMode && (
        <button onClick={handleExitViewingMode}
          className="w-full py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600 transition">
          ← Return to Current Hole ({currentHole}/9)
        </button>
      )}

      {/* Hint */}
      {showHintClue && (
        <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-xl p-3">
          <div className="flex justify-between items-center">
            <div className="text-yellow-300 text-xs font-semibold uppercase tracking-widest">💡 Clue</div>
            <button onClick={() => setHintVisible(v => !v)}
              className="text-yellow-300/60 hover:text-yellow-300 text-xs transition">
              {hintVisible ? "Hide" : "Show"}
            </button>
          </div>
          {hintVisible && <div className="text-white italic mt-1 text-center">{hintClue}</div>}
        </div>
      )}

      <GameBoard
        guesses={guesses}
        currentGuess={isViewingMode ? "" : currentGuess}
        todayWord={targetWord}
        targetWord={targetWord}
        maxGuesses={6}
        revealRowIndex={revealRowIndex}
        shakeRowIndex={shakeRowIndex}
        winningRowIndex={winningRowIndex}
        gameOver={gameOver}
        keyboardStatus={keyboardStatus}
        onKeyPress={isViewingMode ? () => {} : handleKeyPress}
        isRevealing={isRevealing}
        showShareButton={false}
      />

      {gameOver && holeSuccess === false && (
        <div className="bg-white/10 rounded-xl p-3 text-center">
          <span className="text-white/70 text-sm">The word was </span>
          <span className="text-white font-bold text-sm tracking-widest">{targetWord}</span>
        </div>
      )}

      {gameOver && !roundCompleted && !isViewingMode && (
        <button onClick={handleNextHole}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-blue-600 transition">
          {currentHole < 9 ? `➡️ Next Hole (${currentHole + 1}/9)` : "🏁 Finish Round"}
        </button>
      )}

      {showDefinition && targetWord && <WordDefinitionModal word={targetWord} onClose={() => setShowDefinition(false)} />}
    </div>
  );
}
